import type { CreativeAsset, MetaAdDraftInput, MetaAdPublishStatus } from '../../types/metaAdDraft';
import type { LiveAd, LiveAdSet, LiveCampaignDetail } from '../../types/metaLiveCampaigns';
import { buildAttachDraftInput, readCallToAction } from './liveAdSetFlow';

// Express "Add images to a campaign" (Part D): the draft for ONE new ad. Pure.
//
// Each ad is an ordinary "add an ad to a live ad set" draft (so audit, publish, retry and the
// "Added to <ad set>" label all come from the existing path), with the TEMPLATE ad's copy
// untouched and the pair's images as new media. A distinct Story image becomes a placement
// asset rule (Feed image for feed placements, Story image for Story / Reels).

export interface TemplateIssue {
  field: string;
  message: string;
}

/** What stops this ad being a template: copy we could not read, which we will not guess. */
export function templateIssues(template: LiveAd | null, destination: 'website' | 'instant_form' | 'unknown'): TemplateIssue[] {
  if (!template) return [{ field: 'template', message: 'Pick the ad whose copy should be reused.' }];
  const out: TemplateIssue[] = [];
  if (!template.headlines?.length) out.push({ field: 'headline', message: 'The headline could not be read from this ad.' });
  if (!template.primaryText) out.push({ field: 'primaryText', message: 'The primary text could not be read from this ad.' });
  if (!readCallToAction(template.callToAction)) out.push({ field: 'callToAction', message: 'The call to action could not be read from this ad.' });
  if (destination === 'instant_form') {
    if (!template.leadGenFormId) out.push({ field: 'leadForm', message: 'The lead form could not be read from this ad.' });
  } else if (!template.linkUrl) {
    out.push({ field: 'link', message: 'The website link could not be read from this ad.' });
  }
  if (!template.pageId) out.push({ field: 'page', message: "This ad's Facebook Page could not be read." });
  return out;
}

/** Texts the template has beyond the first of each. Only the first is reused; the page says so. */
export function droppedTextCount(template: LiveAd | null): number {
  if (!template) return 0;
  return (
    Math.max(0, (template.bodies?.length ?? 0) - 1) +
    Math.max(0, (template.headlines?.length ?? 0) - 1) +
    Math.max(0, (template.descriptions?.length ?? 0) - 1)
  );
}

const imageAsset = (a: { url: string; path: string }): CreativeAsset => ({
  type: 'image',
  url: a.url,
  path: a.path,
  thumbnailUrl: null,
  thumbnailPath: null,
});

const EMPTY: CreativeAsset = { type: null, url: null, path: null, thumbnailUrl: null, thumbnailPath: null };

export function buildExpressDraftInput(args: {
  accountId: string;
  campaign: LiveCampaignDetail;
  adSet: LiveAdSet;
  template: LiveAd;
  name: string;
  status: MetaAdPublishStatus;
  feed: { url: string; path: string };
  story: { url: string; path: string } | null;
}): MetaAdDraftInput {
  const input = buildAttachDraftInput({ accountId: args.accountId, campaign: args.campaign, adSet: args.adSet, source: args.template });
  input.title = `Added to ${args.adSet.name || 'ad set'}`;
  input.ads.name = args.name.trim();
  input.ads.status = args.status;
  input.ads.creative = {
    ...input.ads.creative,
    assetType: 'image',
    feedAsset: imageAsset(args.feed),
    storyAsset: args.story ? imageAsset(args.story) : { ...EMPTY },
  };
  // New media is the whole point; never the template's own media.
  input.attachTarget = { ...input.attachTarget!, mediaMode: 'new' };
  return input;
}
